import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {
    Divider,
    IconButton,
    TextField,
    Typography,
    FormHelperText,
    Chip,
    Autocomplete
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { updateMmRfqFormItem } from "../../Redux/MMRfq/actions";
import { useFileContext } from "./FileContext";

const priceType = [
    { label: "Company Target Price" },
    { label: "Suggested Price" }
];

export const PartDetails = ({ item, index, isNextButtomClicked }) => {
    const { addFile, removeFile, getFile } = useFileContext();
    const [targetPrice, setTargetPrice] = useState(item?.Target_Price || "");

    const [file, setFile] = useState<File | null>(item?.Upload_Drawing || null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [errorText, setErrorText] = useState<string>("");
    const dispatch = useDispatch();

    const MAX_FILE_SIZE_MB = 50;

    const isValidFile = (file: File): boolean => {
        const allowedTypes = [
            "application/pdf",
            "application/x-rar-compressed",
            "application/zip",
            "application/x-zip-compressed",
            "multipart/x-zip"
        ];
        const isAllowedType = allowedTypes.includes(file.type);
        const isAllowedSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;
        return isAllowedType && isAllowedSize;
    };

    const preventDefault = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const droppedFile = event.dataTransfer.files?.[0];
        if (!droppedFile) return;

        if (!isValidFile(droppedFile)) {
            setErrorText("Only PDF, RAR, ZIP files under 50MB are allowed.");
            setFile(null);
        } else {
            setErrorText("");
            setFile(droppedFile);
            const metadata = {
                name: droppedFile.name,
                size: droppedFile.size,
                type: droppedFile.type
            };
            addFile(item.Part_Number, droppedFile);
            dispatch(
                updateMmRfqFormItem({ ...item, Upload_Drawing: metadata })
            );
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        if (!isValidFile(selectedFile)) {
            setErrorText("Only PDF, RAR, ZIP files under 50MB are allowed.");
            setFile(null);
        } else {
            setErrorText("");
            setFile(selectedFile);
            const metadata = {
                name: selectedFile.name,
                size: selectedFile.size,
                type: selectedFile.type
            };
            addFile(item.Part_Number, selectedFile);
            dispatch(
                updateMmRfqFormItem({ ...item, Upload_Drawing: metadata })
            );
        }
    };

    const handleClearFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        removeFile(item.Part_Number);
        dispatch(updateMmRfqFormItem({ ...item, Upload_Drawing: null }));
    };

    const handleTargetPriceChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow up to 10 digits before decimal and 2 after
        const regex = /^\d{0,10}(\.\d{0,2})?$/;

        if (value === "" || regex.test(value)) {
            setTargetPrice(value);

            const price = parseFloat(value) || 0;
            const qty = parseFloat(item?.Qty_year || "0") || 0;
            const totalOrderCal = (price * qty).toFixed(2);

            dispatch(
                updateMmRfqFormItem({
                    ...item,
                    Target_Price: price,
                    Total_Order_Value: totalOrderCal
                })
            );
        }
    };

    const calculatedTotalOrderValue = () => {
        const price = parseFloat(targetPrice);
        const qty = parseFloat(item?.Qty_year || 0);
        if (isNaN(price) || isNaN(qty)) return "-";
        return (price * qty).toFixed(2);
    };

    useEffect(() => {
        setFile(item?.Upload_Drawing ?? null);
    }, [item?.Upload_Drawing]);

    return (
        <>
            <Typography sx={{ fontWeight: "bold" }} variant="body2" mb={1}>
                Part {index + 1}
            </Typography>

            <Grid container spacing={2} mt={2}>
                {[
                    ["Part ID", item?.Part_ID],
                    ["Part Number", item?.Part_Number],
                    ["Part Name", item?.Part_Name],
                    ["Solutions", item?.Solutions?.join(", ")],
                    // ["Process", item?.Process],
                    ["Raw Material", item?.Raw_Material],
                    ["Quantity/Year", item?.Qty_year],
                    ["Qty/Batch", item?.Qty_Batch],
                    ["UOM", item?.UOM],
                    ["Type of Order", item?.Type_of_Order],
                    ["Mandatory Inspection", item?.Mandatory_Inspection],
                    ["Packing", item?.Packing],
                    ["Sample Required", item?.Sample_Required],
                    ["Target Price (₹)", "targetPriceField"],

                    ["Total Order Value (₹)", calculatedTotalOrderValue()]
                ].map(([label, value], i) => (
                    <Grid key={i} item xs={12} md={3}>
                        {/* <Typography
              variant="body2"
              sx={{ color: "text.secondary", marginBottom: "0.5rem" }}
            >
              {label}
            </Typography> */}

                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                marginBottom: "0.5rem"
                            }}
                        >
                            {label}
                            {/* {label === "Target Price (₹)" ? (
                <>
                  {label} <span style={{ color: "red" }}> *</span>
                </>
              ) : (
                label
              )} */}
                        </Typography>

                        {label === "Total Order Value (₹)" ? (
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={value}
                                disabled
                                size="small"
                                sx={{
                                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                        {
                                            WebkitAppearance: "none",
                                            margin: 0
                                        }
                                }}
                            />
                        ) : label === "Target Price (₹)" ? (
                            <TextField
                                // label={
                                //   <>
                                //     Target Price (₹)<span style={{ color: "red" }}> *</span>
                                //   </>
                                // }
                                placeholder="Enter Price"
                                variant="outlined"
                                size="small"
                                value={targetPrice}
                                onChange={handleTargetPriceChange}
                                fullWidth
                                // error={item?.Target_Price <= 0 && isNextButtomClicked}
                                // helperText={
                                //   item?.Target_Price <= 0 && isNextButtomClicked
                                //     ? "Target Price Required"
                                //     : ""
                                // }
                            />
                        ) : (
                            <Typography variant="body1">
                                {value || "-"}
                            </Typography>
                        )}
                    </Grid>
                ))}
                {/* <Grid item xs={12} md={3} mt={3}>
          <TextField
            label={
              <>
                Target Price (₹)<span style={{ color: "red" }}> *</span>
              </>
            }
            placeholder="Enter Price"
            variant="outlined"
            size="small"
            value={targetPrice}
            onChange={handleTargetPriceChange}
            fullWidth
            sx={{
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
            }}
            error={item?.Target_Price <= 0 && isNextButtomClicked}
            helperText={
              item?.Target_Price <= 0 && isNextButtomClicked
                ? "Target Price Required"
                : ""
            }
          />
        </Grid> */}
            </Grid>

            {/* <Grid item xs={12} md={4} mt={3}>
          <Autocomplete
            disablePortal
            options={priceType}
            renderInput={(params) => (
              <TextField {...params} label="Price Type" />
            )}
          />
        </Grid> */}

            <Grid container spacing={1} my={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="body1" my={1}>
                        Upload Drawing (Part Number - {item?.Part_Number}){" "}
                        <span style={{ color: "red" }}> *</span>
                    </Typography>
                    <label
                        htmlFor={`fileInput-${index}`}
                        style={{ width: "100%", cursor: "pointer" }}
                    >
                        <Box
                            sx={{
                                border: "1px dashed #ccc",
                                borderRadius: "8px",
                                textAlign: "center",
                                padding: "0.5rem 0.5rem 0.7rem",
                                backgroundColor: "#FAFAFA",
                                "&:hover": {
                                    borderColor: "#1976D2"
                                }
                            }}
                            onDrop={handleDrop}
                            onDragOver={preventDefault}
                            onDragEnter={preventDefault}
                        >
                            <CloudUploadIcon
                                sx={{ fontSize: "2.5rem", color: "#1976D2" }}
                            />
                            <Typography variant="body1" gutterBottom>
                                Upload or drag and drop
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                PDF, RAR, ZIP (max. 50MB)
                            </Typography>
                            <input
                                type="file"
                                id={`fileInput-${index}`}
                                style={{ display: "none" }}
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".pdf, .rar, .zip"
                            />
                        </Box>
                    </label>
                    {file && (
                        <Box mt={1}>
                            <Typography variant="body2">
                                File name: {file.name}
                                <IconButton
                                    color="error"
                                    aria-label="remove file"
                                    onClick={handleClearFile}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Typography>
                        </Box>
                    )}
                    {errorText && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            {errorText}
                        </FormHelperText>
                    )}
                    {item?.Upload_Drawing <= 0 && isNextButtomClicked && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            Upload Drawing is Required
                        </FormHelperText>
                    )}
                </Grid>
            </Grid>
        </>
    );
};
